import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Footer from "./Footer";

// import logoImg from "../assets/app/logo.png";

/* ═══════════ BEAUTABLES PALETTE (sampled from the box artwork) ═══════════ */
const INK = "#000";
const LIME = "#C0DC6C";
const RASP = "#FB2564";
const YEL = "#F8DA43";
const RED = "#E10000";

const LIME_GLOSS = `linear-gradient(180deg, #E2F2A9 0%, ${LIME} 52%, #A3C650 100%)`;

/* the box-art flower: four petals and a yellow centre */
const Flower: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="-100 -100 200 200" aria-hidden="true">
    {[0, 90, 180, 270].map((r) => (
      <ellipse key={r} cx="0" cy="-52" rx="34" ry="46" fill={RASP} transform={`rotate(${r + 12})`} />
    ))}
    <circle r="27" fill={YEL} />
  </svg>
);

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Jost:wght@300;400;500;600&display=swap');

.cs-page, .cs-page *, .cs-page *::before, .cs-page *::after{ box-sizing:border-box; margin:0; padding:0; font-family:'Jost',system-ui,sans-serif; }
.cs-page{ background:${INK}; color:#fff; }
:where(.cs-page) button{ font-family:inherit; cursor:pointer; border:none; background:none; color:inherit; }

.cs-scope{ position:relative; min-height:100vh; min-height:100dvh; width:100%; overflow:hidden; display:flex; flex-direction:column; background:${INK}; }

/* colour glows on black: raspberry, yellow, lime */
.cs-wash{ position:absolute; inset:0; pointer-events:none;
  background:
    radial-gradient(560px 560px at 10% 22%, rgba(251,37,100,.22), transparent 70%),
    radial-gradient(520px 520px at 92% 18%, rgba(248,218,67,.16), transparent 70%),
    radial-gradient(620px 620px at 62% 108%, rgba(192,220,108,.22), transparent 70%); }

/* the box-art flowers peeking in from the edges */
.cs-flower{ position:absolute; pointer-events:none; z-index:1; }
.cs-flower.a{ width:300px; height:300px; left:-110px; top:34%; transform:rotate(-12deg); }
.cs-flower.b{ width:240px; height:240px; right:-80px; top:8%; transform:rotate(18deg); }
.cs-flower.c{ width:340px; height:340px; right:-120px; bottom:6%; transform:rotate(-8deg); }

/* dark glass header */
.cs-top{ position:relative; z-index:3; display:flex; align-items:center; justify-content:space-between; gap:16px;
  padding:calc(18px + env(safe-area-inset-top)) 28px 18px;
  background:rgba(0,0,0,.55); -webkit-backdrop-filter:blur(22px) saturate(160%); backdrop-filter:blur(22px) saturate(160%);
  border-bottom:1px solid rgba(255,255,255,.14); }
.cs-brand{ font-family:'Cormorant Garamond',serif; font-weight:700; font-size:30px; letter-spacing:.2em; line-height:1; color:#fff; }

/* glossy 3D pills */
.cs-btn{ position:relative; isolation:isolate; overflow:hidden; display:inline-flex; align-items:center; justify-content:center; gap:8px; height:50px; padding:0 28px; border-radius:999px;
  background:${LIME_GLOSS}; border:1px solid ${INK};
  color:${INK} !important; font-size:11.5px; font-weight:600; letter-spacing:.22em; text-transform:uppercase; white-space:nowrap;
  box-shadow:inset 0 2px 2px rgba(255,255,255,.8), inset 0 -4px 6px rgba(0,0,0,.2), 0 5px 0 #6F8B2A, 0 12px 18px -6px rgba(192,220,108,.45);
  transition:background .25s, border-color .25s, color .25s, transform .15s, box-shadow .15s;
  --hg:linear-gradient(180deg,#FF6B93 0%,${RASP} 52%,#C4114A 100%); --hs:${RASP}; --hf:#fff; --he:#8F0636; }
.cs-btn::before{ content:""; position:absolute; z-index:-1; left:7%; right:7%; top:3px; height:46%; border-radius:999px;
  background:linear-gradient(180deg,rgba(255,255,255,.6),rgba(255,255,255,.05)); pointer-events:none; }
.cs-btn:hover{ background:var(--hg); border-color:var(--he); color:var(--hf) !important; transform:translateY(-2px);
  box-shadow:inset 0 2px 2px rgba(255,255,255,.7), inset 0 -4px 6px rgba(0,0,0,.2), 0 7px 0 var(--he), 0 16px 22px -6px color-mix(in srgb, var(--hs) 60%, transparent); }
.cs-btn:active{ transform:translateY(4px);
  box-shadow:inset 0 2px 2px rgba(255,255,255,.6), inset 0 -3px 5px rgba(0,0,0,.2), 0 1px 0 var(--he), 0 4px 8px -4px rgba(0,0,0,.5); }
.cs-btn.ghost{ background:linear-gradient(180deg,#4a4a4a 0%,#161616 50%,#000 100%); border-color:rgba(255,255,255,.85); color:#fff !important;
  box-shadow:inset 0 2px 2px rgba(255,255,255,.35), inset 0 -4px 6px rgba(0,0,0,.55), 0 5px 0 rgba(255,255,255,.85), 0 12px 18px -6px rgba(0,0,0,.6);
  --hg:linear-gradient(180deg,#FFF27A 0%,${YEL} 52%,#E0B800 100%); --hs:${YEL}; --hf:${INK}; --he:#B89500; }
.cs-btn.ghost::before{ background:linear-gradient(180deg,rgba(255,255,255,.4),rgba(255,255,255,.03)); }
.cs-btn:focus-visible{ outline:3px solid #fff; outline-offset:3px; }

/* centre */
.cs-center{ position:relative; z-index:2; flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; padding:40px 24px 110px; }

/* the one moment: a glossy orb cycling lime, raspberry, yellow, red */
.cs-orb{ --c:${LIME}; position:relative; isolation:isolate; width:180px; height:180px; margin-bottom:46px; border-radius:50%;
  display:flex; align-items:center; justify-content:center; color:${INK};
  background:radial-gradient(circle at 35% 28%, color-mix(in srgb, var(--c) 60%, #fff) 0, var(--c) 45%, color-mix(in srgb, var(--c) 55%, #000) 100%);
  box-shadow:inset -14px -18px 30px rgba(0,0,0,.4), inset 7px 7px 18px rgba(255,255,255,.4), 0 0 0 5px ${INK}, 0 0 0 8px #fff, 0 30px 50px -14px color-mix(in srgb, var(--c) 55%, transparent);
  animation:csHue 8s ease-in-out infinite, csFloat 5s ease-in-out infinite; }
.cs-orb::before{ content:""; position:absolute; z-index:2; top:6%; left:14%; width:52%; height:34%; border-radius:50%;
  background:linear-gradient(180deg, rgba(255,255,255,.9), rgba(255,255,255,.05)); pointer-events:none; }
.cs-orb::after{ content:""; position:absolute; z-index:2; right:15%; bottom:11%; width:26%; height:13%; border-radius:50%; background:rgba(255,255,255,.4);
  transform:rotate(-38deg); filter:blur(1px); pointer-events:none; }
.cs-orb-in{ position:relative; z-index:1; width:100%; height:100%; border-radius:50%; overflow:hidden; display:flex; align-items:center; justify-content:center; }
.cs-orb-in img{ width:60%; height:60%; object-fit:contain; filter:drop-shadow(0 3px 3px rgba(0,0,0,.35)); }
.cs-orb-b{ font-family:'Cormorant Garamond',serif; font-weight:700; font-size:92px; line-height:1; text-shadow:0 2px 0 rgba(255,255,255,.35); }
@property --c{ syntax:'<color>'; inherits:false; initial-value:${LIME}; }
@keyframes csHue{ 0%,100%{ --c:${LIME}; } 25%{ --c:${RASP}; } 50%{ --c:${YEL}; } 75%{ --c:${RED}; } }
@keyframes csFloat{ 0%,100%{ transform:translateY(0); } 50%{ transform:translateY(-8px); } }

.cs-head{ font-family:'Cormorant Garamond',serif; font-weight:700; font-size:92px; line-height:1.0; letter-spacing:-.01em; max-width:900px; margin-bottom:26px; color:#fff; }
.cs-sub{ font-weight:300; font-size:18px; line-height:1.7; color:rgba(255,255,255,.72); max-width:460px; }

/* four glossy dots */
.cs-dots{ display:flex; gap:14px; margin:30px 0 34px; }
.cs-dot{ width:22px; height:22px; border-radius:50%; border:1px solid #fff;
  background:radial-gradient(circle at 35% 28%, color-mix(in srgb, var(--c) 55%, #fff) 0, var(--c) 50%, color-mix(in srgb, var(--c) 60%, #000) 100%);
  box-shadow:inset -3px -4px 6px rgba(0,0,0,.3), inset 1px 1px 3px rgba(255,255,255,.6), 0 3px 0 rgba(255,255,255,.85); }

.cs-actions{ display:flex; gap:16px; flex-wrap:wrap; justify-content:center; }

@media (max-width:768px){
  .cs-top{ padding:calc(14px + env(safe-area-inset-top)) 16px 14px; }
  .cs-brand{ font-size:21px; letter-spacing:.16em; }
  .cs-head{ font-size:52px; }
  .cs-sub{ font-size:16px; }
  .cs-flower.a{ width:200px; height:200px; left:-90px; }
  .cs-flower.b{ width:160px; height:160px; right:-60px; }
  .cs-flower.c{ width:220px; height:220px; right:-90px; }
  .cs-orb{ width:132px; height:132px; margin-bottom:34px; box-shadow:inset -10px -13px 22px rgba(0,0,0,.4), inset 5px 5px 13px rgba(255,255,255,.4), 0 0 0 4px ${INK}, 0 0 0 6px #fff, 0 22px 36px -12px color-mix(in srgb, var(--c) 55%, transparent); }
  .cs-orb-b{ font-size:66px; }
  .cs-btn{ height:44px; padding:0 20px; font-size:10.5px; }
  .cs-actions{ flex-direction:column; align-items:stretch; width:100%; max-width:300px; }
}
@media (prefers-reduced-motion: reduce){
  .cs-page *, .cs-page *::before, .cs-page *::after{ animation:none !important; transition:none !important; }
}
`;

/* ═══════════════════════════════════════════════════════════
   COMING SOON
═══════════════════════════════════════════════════════════ */
const ComingSoon: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="cs-page">
      <style>{CSS}</style>

      <section className="cs-scope">
        <div className="cs-wash" />
        <Flower className="cs-flower a" />
        <Flower className="cs-flower b" />
        <Flower className="cs-flower c" />

        {/* ── TOP BAR ── */}
        <header className="cs-top">
          <motion.span
            initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}
            className="cs-brand"
          >
            BEAUTABLES
          </motion.span>

          <motion.button
            type="button"
            initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}
            className="cs-btn"
            onClick={() => navigate("/auth")}
          >
            Sign up <ArrowRight size={14} />
          </motion.button>
        </header>

        {/* ── CENTER ── */}
        <div className="cs-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 18 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="cs-orb" aria-hidden="true">
              <div className="cs-orb-in">
                {/* <img src={logoImg} alt="Beautables" /> */}
                <span className="cs-orb-b">B</span>
              </div>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="cs-head"
          >
            Cool stuff<br />coming soon
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
            className="cs-sub"
          >
            We're putting the finishing touches on something worth the wait.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.45 }}
            className="cs-dots" aria-hidden="true"
          >
            {[LIME, RASP, YEL, RED].map((c) => (
              <span key={c} className="cs-dot" style={{ ["--c" as any]: c }} />
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}
            className="cs-actions"
          >
            <button type="button" className="cs-btn" onClick={() => navigate("/auth")}>
              Get early access <ArrowRight size={14} />
            </button>
            <button type="button" className="cs-btn ghost" onClick={() => navigate("/shop")}>
              Peek at the shop
            </button>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER (very bottom) ── 
           <Footer />
      
      */}
 
    </div>
  );
};

export default ComingSoon;